// Browser analytics client (issue #343 / ADR-0020).
//
// A small buffered client that emits `page_view` and `interaction` events,
// batches them in-memory, and flushes to the server ingest endpoint:
//   * on an interval,
//   * on a buffer-size threshold,
//   * on `pagehide` / `visibilitychange`→hidden (so the final actions of a visit
//     are not lost).
//
// The client supplies CONTENT only — never identity. The server re-derives
// actor / persona / ip / user-agent from the session at ingest, so nothing here
// is trusted for identity (the trust boundary). Capture is best-effort: a failed
// send is swallowed and never surfaces to the user, and never blocks a page.

import type { AnalyticsClientEvent, AnalyticsClientEventKind } from '@septima/contract';
import { clockNow } from '@/lib/clock';

// Same base URL resolution as the typed api client (lib/api.ts): empty in dev
// (same-origin via the Vite /api proxy) and the absolute API origin in prod.
const BASE_URL = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
const INGEST_URL = `${BASE_URL}/api/analytics/ingest`;

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 20;
/** Hard cap so a long-lived tab that can't reach the server never grows unbounded. */
const MAX_BUFFER = 500;
const SESSION_KEY = 'septima-analytics-session';

let buffer: AnalyticsClientEvent[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let listenersBound = false;

export interface TrackPayload {
    path?: string;
    route?: string;
    objectType?: string;
    objectId?: string;
    details?: Record<string, unknown>;
}

/** A stable per-visit id (sessionStorage-backed). Survives navigations, not tabs. */
function sessionId(): string {
    try {
        let id = sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            id = `sess-${crypto.randomUUID()}`;
            sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        // Private mode / disabled storage — degrade to a non-persistent id.
        return 'sess-unavailable';
    }
}

function currentPath(): string {
    return typeof window !== 'undefined' ? window.location.pathname : '';
}

/** Buffer one event. Drops oldest past the hard cap. Never throws. */
export function track(kind: AnalyticsClientEventKind, payload: TrackPayload = {}): void {
    const event: AnalyticsClientEvent = {
        kind,
        occurredAt: clockNow().toISOString(),
        sessionId: sessionId(),
        path: payload.path ?? currentPath(),
    };
    if (payload.route !== undefined) event.route = payload.route;
    if (payload.objectType !== undefined) event.objectType = payload.objectType;
    if (payload.objectId !== undefined) event.objectId = payload.objectId;
    if (payload.details !== undefined) event.details = payload.details;

    buffer.push(event);
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    if (buffer.length >= FLUSH_THRESHOLD) flush();
}

/** Convenience for the page-view signal (route change). */
export function trackPageView(pathname: string): void {
    track('page_view', { path: pathname });
}

/** Send the buffered events. Best-effort; swallows all errors. */
export function flush(): void {
    if (buffer.length === 0) return;
    const events = buffer;
    buffer = [];
    const body = JSON.stringify({ events });

    // Prefer fetch + keepalive: it survives page unload AND works cross-origin
    // with credentials + CORS (unlike sendBeacon, which cannot preflight an
    // application/json body). sendBeacon is the fallback for browsers without
    // keepalive support.
    if (typeof fetch === 'function') {
        try {
            void fetch(INGEST_URL, {
                method: 'POST',
                credentials: 'include',
                keepalive: true,
                headers: { 'Content-Type': 'application/json' },
                body,
            }).catch(() => undefined);
            return;
        } catch {
            /* fall through to sendBeacon */
        }
    }
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
            navigator.sendBeacon(INGEST_URL, new Blob([body], { type: 'application/json' }));
        } catch {
            /* best-effort — drop */
        }
    }
}

// Delegated click listener — broad, declarative coverage. Any element carrying a
// `data-track` attribute records an `interaction` when clicked; `data-track-*`
// dataset entries ride along in `details`.
function onDocumentClick(e: MouseEvent): void {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest<HTMLElement>('[data-track]');
    if (!el) return;
    const action = el.dataset.track;
    if (!action) return;
    const details: Record<string, unknown> = { action };
    for (const [key, value] of Object.entries(el.dataset)) {
        if (key !== 'track' && key.startsWith('track')) {
            // data-track-foo -> trackFoo -> foo
            const name = key.slice('track'.length);
            details[name.charAt(0).toLowerCase() + name.slice(1)] = value;
        }
    }
    track('interaction', { details });
}

function onVisibilityChange(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') flush();
}

/** Start the interval timer + unload/visibility flush + data-track listener. Idempotent. */
export function startAnalytics(): void {
    if (listenersBound) return;
    listenersBound = true;
    timer = setInterval(flush, FLUSH_INTERVAL_MS);
    if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', flush);
        window.addEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('click', onDocumentClick, { capture: true });
    }
}

/** Tear down (flushes the tail). Idempotent. */
export function stopAnalytics(): void {
    if (!listenersBound) return;
    listenersBound = false;
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
    if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', flush);
        window.removeEventListener('visibilitychange', onVisibilityChange);
        document.removeEventListener('click', onDocumentClick, { capture: true });
    }
    flush();
}
